import React, { useContext } from 'react';
import isEmpty from 'lodash/isEmpty';
import { Typography, Box } from '@mui/material';
import { useFormState } from 'react-final-form';

import {
  LinearProgress,
  Map,
  Card,
  CardContent,
  CardHeader,
  ChipBox,
  ChipType,
} from '@occc-app/design-system';
import { SiteManagementLocationFragment } from '@occc/graphql';

import { LocationContext } from './LocationProvider';
import { LocationFormRenderProps, LocationUpdateFormData } from './types';
import { LocationSiteInfoDetails } from './LocationSiteInfoDetails';
import { LocationAddressDetails } from './LocationAddressDetails';

import { getLoyaltyHostLabel, getSiteSystemDetailsFromLocation, getStatusLabel } from './utils';
import { LOCATION_ATTRIBUTE_VALUE } from '../constants';

export const LocationUpdateDetailsForm = ({
  data,
}: Pick<LocationFormRenderProps, 'handleSubmit'> & {
  data: SiteManagementLocationFragment | null;
}) => {
  const state = useFormState<LocationUpdateFormData>();
  const siteId = state.values?.siteSystemConfiguration?.identifier || '';
  const loyaltyEnabled = state.values?.siteSystemConfiguration?.loyaltyEnabled || false;
  const mobileEnabled = state.values?.siteSystemConfiguration?.mobileEnabled || false;
  const loyaltyHeartbeatsOnlyEnabled =
    state.values?.siteSystemConfiguration?.loyaltyHeartbeatsOnlyEnabled || false;
  const payAtPumpEnabled = state.values?.payAtPumpEnabled || false;
  const pluginConfiguration = state.values?.siteSystemConfiguration?.hardwareVendor || null;
  const { loyaltyProtocol, loyaltyVersion, mobileProtocol, mobileVersion, mobileSiteSystem } =
    getSiteSystemDetailsFromLocation(data);

  const programId =
    data?.rewardPrograms
      ?.map((program) => program?.programId)
      .filter(Boolean)
      .join(', ') || '';

  const loyaltyStatusLabel = getStatusLabel({
    isEnabled: loyaltyEnabled,
    protocol: loyaltyProtocol,
    version: loyaltyVersion,
  });

  const mobileStatusLabel = getStatusLabel({
    isEnabled: mobileEnabled,
    protocol: mobileProtocol,
    version: mobileVersion,
  });

  const loyaltyHostLabel = getLoyaltyHostLabel(data?.rewardPrograms);

  const geoLocation = state.values?.address?.geoLocation;
  const longitude = geoLocation?.longitude;
  const latitude = geoLocation?.latitude;

  const { loading } = useContext(LocationContext);
  const attributes: ChipType[] =
    data?.attributes
      .filter((item) => item.value === LOCATION_ATTRIBUTE_VALUE.YES && !isEmpty(item.name.trim()))
      .map(({ name }, index) => ({ name, id: `${name}${index}` })) || [];

  return (
    <div>
      <Box mb={1}>
        <Card>
          {loading && <LinearProgress />}
          <CardHeader title={<Typography variant='h6'>Site Info</Typography>} />
          <CardContent>
            <LocationSiteInfoDetails
              siteId={siteId}
              loyaltyStatusLabel={loyaltyStatusLabel}
              mobileStatusLabel={mobileStatusLabel}
              loyaltyHeartbeatsOnlyEnabled={loyaltyHeartbeatsOnlyEnabled}
              payAtPumpEnabled={payAtPumpEnabled}
              pluginConfiguration={pluginConfiguration}
              mobileSiteSystem={mobileSiteSystem}
              siteBrandTypeLabel={programId}
              loyaltyHostLabel={loyaltyHostLabel}
            />
          </CardContent>
        </Card>
      </Box>

      <Box mb={1}>
        <Card>
          {loading && <LinearProgress />}
          <CardHeader title={<Typography variant='h6'>Address</Typography>} />
          <CardContent>
            <LocationAddressDetails data={data} />
          </CardContent>
        </Card>
      </Box>
      <Map longitude={longitude} latitude={latitude} withBorder />
      {attributes && attributes.length > 0 && (
        <Card>
          {loading && <LinearProgress />}
          <CardHeader title={<Typography variant='h6'>Attributes</Typography>} />
          <CardContent>
            <ChipBox items={attributes} />
          </CardContent>
        </Card>
      )}
    </div>
  );
};
